import type { Account } from '@api/hooks/useAccounts';
import { Skeleton } from '@components/skeleton/Skeleton';
import {
	ChevronIcon,
	DropdownPositions,
	Loader,
	Pagination,
	SuccessIcon,
	Typography,
} from '@gurupay/gp-ui-library';
import type { PaginatedAccountsResult } from '@hooks/usePaginatedAccounts';
import clsx from 'clsx';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AccountItem } from './AccountItem';

interface AccountListProps {
	options?: Account[];
	value?: Account;
	label?: string;
	onChange: (account: Account) => void;
	pagination: PaginatedAccountsResult;
	isLoading?: boolean;
	disabledAccounts?: (string | number)[];
	isDisabled?: boolean;
	hideBalance?: boolean;
}

export const AccountList: FC<AccountListProps> = ({
	options = [],
	onChange,
	label,
	value,
	pagination,
	isLoading,
	disabledAccounts = [],
	isDisabled,
	hideBalance,
}) => {
	const { t } = useTranslation();
	const [isOpened, setIsOpened] = useState(false);
	const shouldLoadSelection = !value && isLoading;
	const handleSelect = (account: Account) => {
		setIsOpened(false);
		onChange(account);
	};

	const toggleDropdown = () => !isDisabled && setIsOpened((state) => !state);
	const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			toggleDropdown();
		}
	};

	if (!isLoading && !options.length) {
		return null;
	}

	return (
		<section>
			{label && (
				<Typography className="mb-space-8" level="h3">
					{label}
				</Typography>
			)}

			<div className="border-1 border-neutral-100 rounded-lg px-space-8 overflow-auto w-full block py-space-4">
				<div className="flex flex-col min-mobile:min-w-[700px] gap-space-8">
					<div
						className={clsx(
							`grid items-center gap-space-16 p-space-16 transition cursor-pointer
							max-[480px]:grid-cols-[16px_1fr_16px]
  						max-mobile:[&>*:first-child]:col-[1]
  						max-mobile:[&>*:first-child]:row-[1]
  						max-mobile:[&>*:last-child]:row-[1]
  						max-mobile:[&>*:not(:first-child)]:col-[2]
  					`,
							hideBalance
								? 'grid-cols-[20px_auto_1fr_70px_32px] max-mobile:[&>*:nth-child(5)]:col-[3]'
								: 'grid-cols-[20px_auto_1fr_70px_150px_32px] max-mobile:[&>*:nth-child(6)]:col-[3]',
						)}
						onClick={toggleDropdown}
						onKeyDown={onKeyDown}
						role="button"
						tabIndex={0}
					>
						{shouldLoadSelection ? (
							<span />
						) : (
							<SuccessIcon className="w-[18px] h-[18px] text-brand-secondary-700" />
						)}

						<div>
							<div className="text-sm-medium text-neutral-800">
								{t('common.accountHeaders.title')}
							</div>
							{shouldLoadSelection ? (
								<Skeleton className="h-space-16 !mt-space-4" />
							) : (
								<div className="text-sm-bold">{value?.ownerName}</div>
							)}
						</div>

						<div>
							<div className="text-sm-medium text-neutral-800">
								{t('common.accountHeaders.number')}
							</div>
							{shouldLoadSelection ? (
								<Skeleton className="h-space-16 !mt-space-4" />
							) : (
								<div className="text-sm-bold">{value?.iban}</div>
							)}
						</div>

						<div className={clsx(hideBalance && 'min-mobile:text-right')}>
							<div className="text-sm-medium text-neutral-800">
								{t('common.accountHeaders.currency')}
							</div>
							{shouldLoadSelection ? (
								<Skeleton className="h-space-16 !mt-space-4" />
							) : (
								<div className="text-sm-bold">{value?.currency}</div>
							)}
						</div>

						{!hideBalance && (
							<div className="min-mobile:text-right">
								<div className="text-sm-medium text-neutral-800">
									{t('common.accountHeaders.balance')}
								</div>
								{shouldLoadSelection ? (
									<Skeleton className="h-space-16 !mt-space-4" />
								) : (
									<div className="text-sm-bold">{value?.availableBalance ?? '-'}</div>
								)}
							</div>
						)}
						<div className="flex justify-end">
							<ChevronIcon
								className={clsx(
									'transition-all duration-200 ease-linear',
									isOpened ? 'rotate-180' : 'rotate-270',
								)}
							/>
						</div>
					</div>

					{isOpened && (
						<>
							<hr className="mt-[-8px] mb-space-8 text-neutral-100 bg-neutral-100 mx-space-8" />

							{isLoading ? (
								<Loader className="mx-auto my-space-24" />
							) : (
								options.map((option, index) => (
									<AccountItem
										key={option?.uuid ?? index}
										account={option}
										hideBalance={hideBalance}
										isDisabled={disabledAccounts.includes(option.uuid)}
										isSelected={value?.uuid === option.uuid}
										onSelect={handleSelect}
									/>
								))
							)}
							{pagination.totalCount > pagination.pageSize && (
								<Pagination
									currentPage={pagination.currentPage}
									dropdownOpenPosition={DropdownPositions.TOP}
									onPageChange={pagination.onPageChange}
									onPageSizeChange={pagination.onPageSizeChange}
									pageOptions={pagination.pageOptions}
									pageSize={pagination.pageSize}
									pageText={pagination.pageText}
									totalCount={pagination.totalCount}
									totalCountText={pagination.totalCountText}
								/>
							)}
						</>
					)}
				</div>
			</div>
		</section>
	);
};
